import {
  DEFAULT_REPOSITORY_INDEXING_MODE,
  type RepositoryIndexingMode,
} from "@arti/shared-source-control/indexing/runMode";
import { type FC, useEffect, useMemo, useState } from "react";

import type { SourceControlRepository } from "../../types";
import type { RepositoryIndexingRunRepositoryModeSelection } from "../../types/repositoryIndexing";

import { fetchRepositoryIndexingFailedRepositorySlugs } from "../../api/sourceControl";
import { formatErrorMessage } from "../../utils/errors";
import { resolveMissingProvidersForSelections } from "../../utils/repositoryIndexingModes";
import { Button } from "../common/Button";
import { Modal } from "../common/Modal";
import { IndexingRunModeGuide } from "./IndexingRunModeGuide";
import { IndexingRunRepositoryRow } from "./IndexingRunRepositoryRow";
import { IndexingRunSelectionControls } from "./IndexingRunSelectionControls";
import { IndexingRunStatusBanner } from "./IndexingRunStatusBanner";
interface RepositoryOption {
  normalizedSlug: string;
  slug: string;
}

interface IndexingRunConfigModalProps {
  isOpen: boolean;
  latestRunId: string | null;
  missingProviders: string[];
  /** When set, only this repo is pre-selected instead of all repos. */
  preselectedSlug?: string | null;
  repositories: SourceControlRepository[];
  submitting: boolean;
  submitError?: string | null;
  onClose: () => void;
  onSubmit: (
    repositories: RepositoryIndexingRunRepositoryModeSelection[],
  ) => Promise<void> | void;
}

export const IndexingRunConfigModal: FC<IndexingRunConfigModalProps> = ({
  isOpen,
  latestRunId,
  missingProviders,
  preselectedSlug = null,
  repositories,
  submitting,
  submitError = null,
  onClose,
  onSubmit,
}) => {
  const repositoryOptions = useMemo(() => {
    const bySlug = new Map<string, RepositoryOption>();
    for (const repository of repositories) {
      const slug = repository.remote_slug.trim();
      if (!slug) {
        continue;
      }
      const normalizedSlug = slug.trim().toLowerCase();
      if (!bySlug.has(normalizedSlug)) {
        bySlug.set(normalizedSlug, { normalizedSlug, slug });
      }
    }
    return Array.from(bySlug.values()).toSorted((left, right) =>
      left.slug.localeCompare(right.slug),
    );
  }, [repositories]);

  const [bulkMode, setBulkMode] = useState<RepositoryIndexingMode>(
    DEFAULT_REPOSITORY_INDEXING_MODE,
  );
  const [modeBySlug, setModeBySlug] = useState<
    Record<string, RepositoryIndexingRunRepositoryModeSelection["mode"]>
  >({});
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(new Set());
  const [selectionMessage, setSelectionMessage] = useState<string | null>(null);
  const [selectingFailed, setSelectingFailed] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setBulkMode(DEFAULT_REPOSITORY_INDEXING_MODE);
    setModeBySlug(
      Object.fromEntries(
        repositoryOptions.map((repository) => [
          repository.normalizedSlug,
          DEFAULT_REPOSITORY_INDEXING_MODE,
        ]),
      ),
    );
    if (preselectedSlug) {
      const normalized = preselectedSlug.trim().toLowerCase();
      const match = repositoryOptions.find(
        (r) => r.normalizedSlug === normalized,
      );
      setSelectedSlugs(match ? new Set([match.normalizedSlug]) : new Set());
    } else {
      setSelectedSlugs(
        new Set(
          repositoryOptions.map((repository) => repository.normalizedSlug),
        ),
      );
    }
    setSelectionMessage(null);
    setSelectingFailed(false);
  }, [isOpen, preselectedSlug, repositoryOptions]);

  const selectedRepositories = useMemo(
    () =>
      repositoryOptions
        .filter((repository) => selectedSlugs.has(repository.normalizedSlug))
        .map((repository) => ({
          mode:
            modeBySlug[repository.normalizedSlug] ??
            DEFAULT_REPOSITORY_INDEXING_MODE,
          repository_slug: repository.slug,
        })),
    [modeBySlug, repositoryOptions, selectedSlugs],
  );

  const blockingMissingProviders = useMemo(
    () =>
      resolveMissingProvidersForSelections(
        selectedRepositories,
        missingProviders,
      ),
    [missingProviders, selectedRepositories],
  );

  if (!isOpen) {
    return null;
  }

  const handleSelectAll = () => {
    setSelectionMessage(null);
    setSelectedSlugs(
      new Set(repositoryOptions.map((repo) => repo.normalizedSlug)),
    );
  };

  const handleClearAll = () => {
    setSelectionMessage(null);
    setSelectedSlugs(new Set());
  };

  const handleApplyBulkMode = () => {
    setSelectionMessage(null);
    setModeBySlug((current) => {
      const next = { ...current };
      for (const normalizedSlug of selectedSlugs) {
        next[normalizedSlug] = bulkMode;
      }
      return next;
    });
  };

  const handleSelectFailedFromLastRun = async () => {
    if (!latestRunId) {
      setSelectionMessage("No previous run is available yet.");
      return;
    }
    setSelectingFailed(true);
    setSelectionMessage(null);
    try {
      const failedSlugs =
        await fetchRepositoryIndexingFailedRepositorySlugs(latestRunId);
      const failedSlugSet = new Set(
        failedSlugs.map((slug) => slug.trim().toLowerCase()),
      );
      const nextSelection = new Set<string>();
      for (const repository of repositoryOptions) {
        if (failedSlugSet.has(repository.normalizedSlug)) {
          nextSelection.add(repository.normalizedSlug);
        }
      }
      if (nextSelection.size === 0) {
        setSelectionMessage(
          "Latest run has no failed repositories in the current repository list.",
        );
        return;
      }
      setSelectedSlugs(nextSelection);
      setSelectionMessage(
        `Selected ${nextSelection.size} failed repos from the latest run.`,
      );
    } catch (error) {
      setSelectionMessage(formatErrorMessage(error));
    } finally {
      setSelectingFailed(false);
    }
  };

  const handleSubmit = async () => {
    if (selectedRepositories.length === 0) {
      setSelectionMessage("Select at least one repository.");
      return;
    }
    if (blockingMissingProviders.length > 0) {
      setSelectionMessage(
        `Selected modes require missing providers: ${blockingMissingProviders.join(", ")}.`,
      );
      return;
    }
    await onSubmit(selectedRepositories);
  };

  return (
    <Modal
      title="Trigger Repository Indexing Run"
      subtitle="Select repositories and choose whether each should run embeddings, indexing, or both."
      onClose={onClose}
      widthClass="max-w-5xl"
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSubmit()}
            disabled={
              submitting ||
              repositoryOptions.length === 0 ||
              blockingMissingProviders.length > 0
            }
            loading={submitting}
            loadingText="Triggering..."
          >
            Trigger Run
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <IndexingRunSelectionControls
          bulkMode={bulkMode}
          hasLatestRun={Boolean(latestRunId)}
          repositoryCount={repositoryOptions.length}
          selectedCount={selectedRepositories.length}
          selectingFailed={selectingFailed}
          selectedSlugCount={selectedSlugs.size}
          submitting={submitting}
          onApplyBulkMode={handleApplyBulkMode}
          onBulkModeChange={setBulkMode}
          onClearAll={handleClearAll}
          onSelectAll={handleSelectAll}
          onSelectFailedFromLastRun={() => void handleSelectFailedFromLastRun()}
        />

        <IndexingRunModeGuide />

        {selectionMessage ? (
          <IndexingRunStatusBanner tone="neutral">
            {selectionMessage}
          </IndexingRunStatusBanner>
        ) : null}

        {blockingMissingProviders.length > 0 ? (
          <IndexingRunStatusBanner tone="warning">
            {`Selected modes require unavailable providers: ${blockingMissingProviders.join(", ")}. Switch those repos to a compatible mode or add the missing org API keys.`}
          </IndexingRunStatusBanner>
        ) : null}

        {submitError ? (
          <IndexingRunStatusBanner tone="error">
            {submitError}
          </IndexingRunStatusBanner>
        ) : null}

        {repositoryOptions.length === 0 ? (
          <IndexingRunStatusBanner tone="neutral">
            No repositories are currently configured for indexing.
          </IndexingRunStatusBanner>
        ) : (
          <div className="custom-scrollbar max-h-[32rem] overflow-y-auto border border-slate-300 dark:border-slate-700">
            <ul className="divide-y divide-slate-200 dark:divide-slate-700">
              {repositoryOptions.map((repository) => {
                const checked = selectedSlugs.has(repository.normalizedSlug);
                const mode =
                  modeBySlug[repository.normalizedSlug] ??
                  DEFAULT_REPOSITORY_INDEXING_MODE;

                return (
                  <IndexingRunRepositoryRow
                    key={repository.normalizedSlug}
                    checked={checked}
                    disabled={submitting}
                    mode={mode}
                    repositorySlug={repository.slug}
                    onCheckedChange={(nextChecked) => {
                      setSelectionMessage(null);
                      setSelectedSlugs((current) => {
                        const next = new Set(current);
                        if (nextChecked) {
                          next.add(repository.normalizedSlug);
                        } else {
                          next.delete(repository.normalizedSlug);
                        }
                        return next;
                      });
                    }}
                    onModeChange={(nextMode) => {
                      setSelectionMessage(null);
                      setModeBySlug((current) => ({
                        ...current,
                        [repository.normalizedSlug]: nextMode,
                      }));
                    }}
                  />
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
};
